import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Devices from './pages/Devices';
import VPN from './pages/VPN';
import NAS from './pages/NAS';
import CCTV from './pages/CCTV';
import Users from './pages/Users';
import UserActivity from './pages/UserActivity';
import UserUpload from './pages/UserUpload';
import History from './pages/History';
import Profile from './pages/Profile';
import EmailConfig from './pages/EmailConfig';
import Planner from './pages/Planner';
import Kadry from './pages/Kadry';
import Dokumenty from './pages/Dokumenty';
import DokumentyModul from './pages/DokumentyModul';
import WniosekUrlopowy from './pages/WniosekUrlopowy';
import WniosekDelegacyjny from './pages/WniosekDelegacyjny';
import WnioskiDelegacyjneLista from './pages/WnioskiDelegacyjneLista';
import Wnioski from './pages/Wnioski';
import Pracownicy from './pages/Pracownicy';
import Nadgodziny from './pages/Nadgodziny';
import WniosekNadgodziny from './pages/WniosekNadgodziny';
import Announcements from './pages/Announcements';
import Tickets from './pages/Tickets';
import LoadingSpinner from './components/LoadingSpinner';

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route 
        path="/login" 
        element={user ? <Navigate to={(user?.role === 'admin' || user?.role === 'super admin') ? '/dashboard' : '/upload'} replace /> : <Login />} 
      />
      
      {/* Protected routes */}
      <Route 
        path="/*" 
        element={
          user ? (
            <Layout>
              <Routes>
                <Route path="/dashboard" element={
                    <Dashboard />
                } />
                <Route path="/devices" element={
                  <ProtectedRoute superAdminOnly={true}>
                    <Devices />
                  </ProtectedRoute>
                } />
                <Route path="/vpn" element={
                  <ProtectedRoute superAdminOnly={true}>
                    <VPN />
                  </ProtectedRoute>
                } />
                <Route path="/nas" element={
                  <ProtectedRoute superAdminOnly={true}>
                    <NAS />
                  </ProtectedRoute>
                } />
                <Route path="/cctv" element={
                  <ProtectedRoute moduleAccess="cctv">
                    <CCTV />
                  </ProtectedRoute>
                } />
                <Route path="/users" element={
                  <ProtectedRoute adminOnly={true}>
                    <Users />
                  </ProtectedRoute>
                } />
                <Route path="/users/aktywnosc" element={
                  <ProtectedRoute adminOnly={true}>
                    <UserActivity />
                  </ProtectedRoute>
                } />
                <Route path="/upload" element={
                  <ProtectedRoute moduleAccess="upload">
                    <UserUpload />
                  </ProtectedRoute>
                } />
                <Route path="/history" element={
                  <ProtectedRoute moduleAccess="history">
                    <History />
                  </ProtectedRoute>
                } />
                <Route path="/dokumenty" element={
                  <ProtectedRoute moduleAccess="dokumenty">
                    <DokumentyModul />
                  </ProtectedRoute>
                } />
                <Route path="/kadry" element={
                  <ProtectedRoute moduleAccessAny={['urlopy', 'delegacje', 'kadry']}>
                    <Kadry />
                  </ProtectedRoute>
                } />
                <Route path="/kadry/pracownicy" element={
                  <ProtectedRoute moduleAccessAny={['urlopy', 'delegacje', 'kadry']} kadryHROnly>
                    <Pracownicy />
                  </ProtectedRoute>
                } />
                <Route path="/kadry/dokumenty" element={
                  <ProtectedRoute moduleAccessAny={['urlopy', 'delegacje', 'kadry']}>
                    <Dokumenty />
                  </ProtectedRoute>
                } />
                <Route path="/kadry/dokumenty/wniosek-urlopowy/:id?" element={
                  <ProtectedRoute moduleAccessAny={['urlopy', 'kadry']}>
                    <WniosekUrlopowy />
                  </ProtectedRoute>
                } />
                <Route path="/kadry/dokumenty/wniosek-delegacyjny/:id?" element={
                  <ProtectedRoute moduleAccessAny={['delegacje', 'kadry']}>
                    <WniosekDelegacyjny />
                  </ProtectedRoute>
                } />
                <Route path="/kadry/wnioski-delegacyjne" element={
                  <ProtectedRoute moduleAccessAny={['delegacje', 'kadry']}>
                    <WnioskiDelegacyjneLista />
                  </ProtectedRoute>
                } />
                <Route path="/kadry/wnioski" element={
                  <ProtectedRoute moduleAccessAny={['urlopy', 'kadry']}>
                    <Wnioski />
                  </ProtectedRoute>
                } />
                <Route path="/kadry/moje-wnioski" element={<Navigate to="/kadry/wnioski?tab=moje" replace />} />
                <Route path="/kadry/wnioski-do-podpisania" element={<Navigate to="/kadry/wnioski?tab=do-podpisania" replace />} />
                <Route path="/kadry/wnioski-podpisane" element={<Navigate to="/kadry/wnioski?tab=podpisane" replace />} />
                <Route path="/nadgodziny" element={<Nadgodziny />} />
                <Route path="/nadgodziny/wniosek/:id?" element={<WniosekNadgodziny />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/announcements" element={<Announcements />} />
                <Route path="/tickets" element={<Tickets />} />
                <Route path="/email-config" element={
                  <ProtectedRoute superAdminOnly={true}>
                    <EmailConfig />
                  </ProtectedRoute>
                } />
                <Route path="/planner" element={
                  <Navigate to="/planner/calendar" replace />
                } />
                <Route path="/planner/calendar" element={
                  <ProtectedRoute moduleAccess="planner">
                    <Planner />
                  </ProtectedRoute>
                } />
                <Route path="/planner/kanban" element={
                  <ProtectedRoute moduleAccess="kanban">
                    <Planner />
                  </ProtectedRoute>
                } />
                <Route path="/planner/teams" element={
                  <ProtectedRoute moduleAccess="ekipaSerwisowa">
                    <Planner />
                  </ProtectedRoute>
                } />
                <Route path="/planner/farms" element={
                  <ProtectedRoute moduleAccess="farmyFoto">
                    <Planner />
                  </ProtectedRoute>
                } />
                <Route path="/planner/map" element={
                  <ProtectedRoute moduleAccessAny={['planner', 'farmyFoto', 'mapa']}>
                    <Planner />
                  </ProtectedRoute>
                } />
                <Route path="/" element={
                  <Navigate to={(user?.role === 'admin' || user?.role === 'super admin') ? '/dashboard' : '/upload'} replace />
                } />
                <Route path="*" element={
                  <Navigate to={(user?.role === 'admin' || user?.role === 'super admin') ? '/dashboard' : '/upload'} replace />
                } />
              </Routes>
            </Layout>
          ) : (
            <Navigate to="/login" replace />
          )
        } 
      />
    </Routes>
  );
}

export default App;
